import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

const PLAYER_TOKEN_KEY = 'access_token'

// Module-level singleton — lazy-initialized on first use
let echoInstance: Echo<'reverb'> | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let reconnectAttempts = 0

const MAX_RECONNECT_DELAY_MS = 30_000
const BASE_RECONNECT_DELAY_MS = 1_000

/**
 * Get the singleton Echo instance, initializing lazily on first call.
 */
function getEcho(): Echo<'reverb'> {
  if (!echoInstance) {
    // laravel-echo's Reverb broadcaster looks for window.Pusher
    window.Pusher = Pusher

    const token = localStorage.getItem(PLAYER_TOKEN_KEY)

    echoInstance = new Echo({
      broadcaster: 'reverb',
      key: import.meta.env.VITE_REVERB_APP_KEY as string,
      wsHost: import.meta.env.VITE_REVERB_HOST as string,
      wsPort: Number(import.meta.env.VITE_REVERB_PORT ?? 80),
      wssPort: Number(import.meta.env.VITE_REVERB_PORT ?? 443),
      forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'https') === 'https',
      enabledTransports: ['ws', 'wss'],
      authEndpoint: '/api/broadcasting/auth',
      auth: {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'application/json',
        },
      },
    })

    // Monitor connection state for auto-reconnect with exponential backoff
    echoInstance.connector.onConnectionChange((status) => {
      if (status === 'connected') {
        reconnectAttempts = 0
        if (reconnectTimer) {
          clearTimeout(reconnectTimer)
          reconnectTimer = null
        }
      } else if (status === 'failed') {
        scheduleReconnect()
      }
    })
  }

  return echoInstance
}

/**
 * Schedule a reconnection attempt with exponential backoff.
 */
function scheduleReconnect(): void {
  if (reconnectTimer) return

  const delay = Math.min(
    BASE_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttempts),
    MAX_RECONNECT_DELAY_MS,
  )
  reconnectAttempts++

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    echoInstance?.connect()
  }, delay)
}

/**
 * Subscribe to a public broadcast channel event.
 */
export function subscribeToPublicChannel<T = unknown>(
  channel: string,
  event: string,
  callback: (data: T) => void,
): void {
  getEcho().channel(channel).listen(event, callback as CallableFunction)
}

/**
 * Subscribe to a private broadcast channel event.
 */
export function subscribeToPrivateChannel<T = unknown>(
  channel: string,
  event: string,
  callback: (data: T) => void,
): void {
  getEcho().private(channel).listen(event, callback as CallableFunction)
}

/**
 * Unsubscribe from a channel (leaves public, private, and presence variants).
 */
export function unsubscribe(channel: string): void {
  getEcho().leave(channel)
}

/**
 * Vue composable returning typed WebSocket subscription helpers.
 */
export function useEcho() {
  return {
    subscribeToPublicChannel,
    subscribeToPrivateChannel,
    unsubscribe,
  }
}
